import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as moment from 'moment';
import { debounceTime, take } from 'rxjs';
import { IconComponent } from 'src/app/shared/components/icon/icon.component';
import { DateComponent } from 'src/app/shared/components/inputs/simple/date/date.component';
import { IntervalListComponent } from '../interval-list/interval-list.component';
import { PipesModule } from 'src/app/shared/pipes/pipe-module';
import { localDB } from 'src/app/shared/helpers/localStorage';
import { AccionAgregada, DeviceInfo, FinalMark, IntervalSelected, IntervalsLoaded, IntervalView, MarksSelected, RequestExtempPayload } from '../../interface/interfaces';
import { computeNewPosition } from '../action-form/validators/validator';
import { SmartAlertService } from 'src/app/shared/components/smart-alert/smart-alert.service';
import { SmartAlertResult } from 'src/app/shared/components/smart-alert/interfaces/custom-dialog.interface';
import { RequestsService } from 'src/app/modules/core/requests/services/request.service';
import { IntervalService } from '../../../interval-marks/service/interval-service.service';
import { DeviceUUID } from "device-uuid";
import { ActionFormComponent } from "../action-form/action-form.component";
import { SpinnerComponent } from "src/app/shared/components/spinner/spinner.component";
import { MatTooltipModule } from "@angular/material/tooltip";
import { GenericResponse } from 'src/app/shared/interfaces/generic.interface';
import { marcasExistentesFueraDeJornada, validarInicioEnOtraJornada, validarRequisitoIntervalos, validarSolapeJornadaVecina } from './validators/validators-extemp';
import { MARKS_ACTIONS } from '../../helpers/enums';
import { REQUEST_TYPE } from 'src/app/shared/interfaces/interface-manager-table';
import { MarkingService } from 'src/app/shared/components/marking/service/marking.service';
import { buildRecommendedMarkPairs } from 'src/app/shared/components/marking/helpers/marks-recommended.helper';

@Component({
  standalone: true,
  selector: 'mg-new-request-extemp',
  templateUrl: './new-request-extemp.component.html',
  styleUrls: ['./new-request-extemp.component.scss'],
  imports: [
    CommonModule,
    IconComponent,
    DateComponent,
    IntervalListComponent,
    PipesModule,
    ReactiveFormsModule,
    ActionFormComponent,
    SpinnerComponent,
    MatTooltipModule
  ],
})
export class NewRequestExtempComponent implements OnInit {
  @Input() minDate: Date;
  @Input() maxDate: Date;
  @Input() disabledDays: string[] = [];
  @Input() fechasIncompletas: string[] = [];
  @Input() fechasSinMarcacion: string[] = [];
  @Input() dateSelected: Date | null = null;
  @Input() marksIntervals: { inicio: string; fin: string }[] = [];
  @Output() goBack = new EventEmitter<void>();

  private readonly _smartAlertService = inject(SmartAlertService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly _intervalService = inject(IntervalService);
  private readonly _requestsService = inject(RequestsService);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _marksService = inject(MarkingService);

  selectedDate: Date = moment().toDate();
  actionDate: moment.Moment = moment();
  moment = moment;
  totalMarcaciones: number = 0;
  nId_Colaborador: number;
  totalPendientes: number = 0;
  intervals: IntervalView[] = [];
  adjacentIntervals: IntervalView[] = [];
  acciones: AccionAgregada[] = [];
  esEliminar: boolean = false;
  hasIntervals: boolean = true;
  isLoadingIntervals: boolean = true;
  spinner: boolean = false;
  markSelected: MarksSelected | null = null;

  uuid = new DeviceUUID().get();
  deviceData = new DeviceUUID().parse();
  trueProps: string[] = [];
  intervalValidationError: string = '';
  debeEliminarMarcaFuera = false;

  fechaControl = new FormControl<Date | null>(null);

  get currentActionType(): 'add' | 'edit' | 'delete' | null {
    if (this.markSelected) {
      if (this.esEliminar) return 'delete';
      if (this.markSelected.sOriginalHour) return 'edit';
      return 'add';
    }
    if (this.acciones.length > 0) return this.acciones[0].actionType;
    return null;
  }

  cannotDeleteMarks: boolean = false;

  get isSelectedDateToday(): boolean {
    const today = new Date();
    const sel = new Date(this.selectedDate);
    return sel.getFullYear() === today.getFullYear() &&
           sel.getMonth() === today.getMonth() &&
           sel.getDate() === today.getDate();
  }

  get effectiveMinMarksCount(): number {
    return this.isSelectedDateToday ? 0 : this.marksIntervals.length * 2;
  }

  onCannotDeleteChange(cannotDelete: boolean): void {
    this.cannotDeleteMarks = !this.isSelectedDateToday && cannotDelete;
  }

  get isDateLocked(): boolean {
    return this.acciones.length > 0 || !!this.markSelected;
  }

  get dateLockTooltip(): string {
    if (this.acciones.length > 0) return 'No puedes cambiar de jornada con acciones agregadas.';
    if (this.markSelected) return 'Finaliza o cancela la acción en curso para cambiar la fecha.';
    return '';
  }
  accionForm = new FormGroup({
    sHour: new FormControl<string>('', Validators.required),
    sReason: new FormControl<string>('', [
      Validators.required,
      Validators.maxLength(650)
    ]),
  });

  get siguienteIntervalInfo(): IntervalSelected {
    if (this.intervals.length === 0) {
      return { nInterval: 1, nType_Mark: 1, label: 'Inicio' };
    }
    const ultimoInterval = this.intervals[this.intervals.length - 1];
    const ultimoIndice = this.intervals.length;
    if (ultimoInterval.inicio && !ultimoInterval.fin) {
      return { nInterval: ultimoIndice, nType_Mark: 2, label: 'Fin' };
    }
    return { nInterval: ultimoIndice + 1, nType_Mark: 1, label: 'Inicio' };
  }

  ngOnInit(): void {
    this.nId_Colaborador = Number(localDB.getItem('idColaborador'));
    const initialDate = this.dateSelected ?? moment().toDate();
    this.fechaControl.setValue(initialDate);
    this.selectedDate = initialDate;

    this.trueProps = Object.entries(this.deviceData)
      .filter(([key, value]) => value === true)
      .map(([key, value]) => key);

    this.fechaControl.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe((val) => {
        this.isLoadingIntervals = true;
        // this.cannotDeleteMarks = false;
        if (val) {
          this.selectedDate = val;
        } else if (this.dateSelected) {
          this.selectedDate = this.dateSelected;
        }
        this.refreshRecommendedMarks(this.selectedDate);
      });
  }

  private refreshRecommendedMarks(fecha: Date): void {
    const formatted = moment(fecha).format('YYYY-MM-DD');
    this._marksService.getMarksRecomended(formatted).pipe(take(1)).subscribe({
      next: (resp) => {
        this.marksIntervals = resp.isSuccess && resp.data ? buildRecommendedMarkPairs(resp.data) : [];
      },
      error: () => {
        this.marksIntervals = [];
      }
    });
  }

  onIntervalsLoaded(event: IntervalsLoaded): void {
    this.hasIntervals = event.intervals.length > 0;
    this.totalMarcaciones = event.totalMarcaciones;
    this.totalPendientes = event.totalPendientes;
    this.intervals = event.intervals;
    this.adjacentIntervals = event.adjacentIntervals ?? [];
    this.actualizarValidacionIntervalos();
  }

  selectedMarks(nInterval: number, nIndex: number | undefined, nType_Mark: number, sHour: string, sOriginalHour?: string, originalDate?: string): void {
    const esMarcaFuera = sHour === '' && marcasExistentesFueraDeJornada(this.selectedDate, this.intervals, this.acciones)
      .some(mark => mark.nInterval === nInterval && mark.nType_Mark === nType_Mark);
    if (esMarcaFuera) {
      const fecha = moment(this.selectedDate).format('DD/MM/YYYY');
      const dialog = this._smartAlertService.init({
        type: 'info',
        title: 'Atención',
        message: `Eliminar la marca de las ${sOriginalHour} generará una solicitud de eliminación aparte de las que ya estás generando para el ${fecha}. Esa solicitud también quedará para el ${fecha}.`,
        showCancelButton: true,
        showConfirmButton: true,
        confirmButtonText: 'Continuar',
        cancelButtonText: 'Cancelar',
      });
      if (!dialog) return;
      dialog.subscribe((result: SmartAlertResult) => {
        if (result.confirmStatus) {
          this.cargarMarcaSeleccionada(nInterval, nIndex, nType_Mark, sHour, sOriginalHour, originalDate);
        }
      });
      return;
    }
    this.cargarMarcaSeleccionada(nInterval, nIndex, nType_Mark, sHour, sOriginalHour, originalDate);
  }

  private cargarMarcaSeleccionada(nInterval: number, nIndex: number | undefined, nType_Mark: number, sHour: string, sOriginalHour?: string, originalDate?: string): void {
    const accionExistente = this.acciones.find(
      a => a.nInterval === nInterval && a.nType_Mark === nType_Mark
    );
    if (accionExistente?.actionType === 'delete' && sHour !== '') return;
    const esEliminar = sHour === '';
    const isNextDayStored = accionExistente
      ? moment(accionExistente.dDate).isAfter(moment(this.selectedDate), 'day')
      : false;
    this.markSelected = {
      nInterval,
      nIndex,
      nType_Mark,
      label: nType_Mark === 1 ? 'Inicio' : 'Fin',
      sOriginalHour: sOriginalHour,
      isNextDay: isNextDayStored
    };
    this.esEliminar = esEliminar;
    this.actionDate = originalDate ? moment(originalDate) : moment(this.selectedDate);
    this.accionForm.reset();
    const horaACargar = accionExistente?.sHour || sHour;
    if (!esEliminar && horaACargar) {
      this.accionForm.get('sHour')?.setValue(horaACargar);
      this.accionForm.get('sReason')?.setValue(accionExistente?.sReason || '');
    }
    this.syncDateLock();
  }

  cancelAction(): void {
    this.markSelected = null;
    this.accionForm.reset();
    this.syncDateLock();
  }

  private syncDateLock(): void {
    if (this.isDateLocked) {
      this.fechaControl.disable({ emitEvent: false });
    } else {
      this.fechaControl.enable({ emitEvent: false });
    }
  }

  addAction(isNextDay: boolean = false): void {
    if (!this.markSelected) return;
    const sReason = this.accionForm.get('sReason')?.value?.trim() ?? '';
    if (!sReason) return;

    const { nInterval, nType_Mark, sOriginalHour } = this.markSelected;

    if (this.esEliminar) {
      const nueva: AccionAgregada = {
        nInterval, nType_Mark, sHour: '', sReason, dDate: '', actionType: 'delete', sOriginalHour
      };
      this.updateActions(nueva);
      this.cancelAction();
      return;
    }

    const sHour = this.accionForm.get('sHour')?.value?.trim() ?? '';
    if (!sHour) return;
    const fechaBaseMoment = moment(this.actionDate);
    if (isNextDay) fechaBaseMoment.add(1, 'days');

    const fechaBase = fechaBaseMoment.format('YYYY-MM-DD');
    const actionType = sOriginalHour ? 'edit' : 'add';
    const nueva: AccionAgregada = {
      nInterval,
      nType_Mark,
      sHour,
      sReason,
      dDate: moment(`${fechaBase} ${sHour}`, 'YYYY-MM-DD HH:mm').toISOString(),
      actionType,
      sOriginalHour: sOriginalHour
    };

    this.updateActions(nueva);
    this.cancelAction();
  }

  private updateActions(nuevaAccion: AccionAgregada): void {
    const idx = this.acciones.findIndex(
      (a) => a.nInterval === nuevaAccion.nInterval &&
        a.nType_Mark === nuevaAccion.nType_Mark &&
        a.actionType === nuevaAccion.actionType
    );
    if (idx >= 0) {
      const nuevasAcciones = [...this.acciones];
      nuevasAcciones[idx] = nuevaAccion;
      this.acciones = nuevasAcciones;
    } else {
      this.acciones = [...this.acciones, nuevaAccion];
    }
    this.actualizarValidacionIntervalos();
  }
  deleteAction(nInterval: number, nType_Mark: number, actionType: string): void {
    this.acciones = this.acciones.filter(
      (a) => !(a.nInterval === nInterval && a.nType_Mark === nType_Mark),
    );
    this.actualizarValidacionIntervalos();
    this.syncDateLock();
  }

  goBackList(): void {
    if (this.acciones.length === 0 && this.markSelected === null) {
      this.goBack.emit();
      return;
    }

    this._smartAlertService.init({
      type: 'quest',
      message: '¿Deseas volver? Perderás las acciones que no hayas enviado.',
      showCancelButton: true,
    }).subscribe((result: SmartAlertResult) => {
      if (result.confirmStatus) {
        this.limpiarAcciones();
        this.goBack.emit();
      }
    });
  }

  getActionMessage(accion: AccionAgregada): string {
    const h = (hour: string) => `<span class="text-mg-main-blue-dark font-bold">${hour}</span>`;

    if (accion.actionType === 'delete') {
      const markType = accion.nType_Mark === 1 ? 'Inicio' : 'Fin';
      const indexToShow = this.getVisualIndex(accion.nInterval);
      const intervalLabel = `<span class="font-semibold text-mg-main-blue-dark">Intervalo ${indexToShow}</span>`;
      return `${intervalLabel} · Eliminación de ${markType} a las ${h(accion.sOriginalHour!)}`;
    }

    const pos = this.getDisplayPosition(accion);
    const markType = pos ? pos.type : (accion.nType_Mark === 1 ? 'Inicio' : 'Fin');
    const indexToShow = pos ? pos.intervalNum : this.getVisualIndex(accion.nInterval);
    const intervalLabel = `<span class="font-semibold text-mg-main-blue-dark">Intervalo ${indexToShow}</span>`;

    if (accion.actionType === 'add') {
      return `${intervalLabel} · Registro de ${markType} a las ${h(accion.sHour)}`;
    }
    return `${intervalLabel} · Edición de ${markType} de las ${h(accion.sOriginalHour!)} a las ${h(accion.sHour)}`;
  }

  private getDisplayPosition(accion: AccionAgregada): { intervalNum: number; type: string } | null {
    if (!accion.sHour) return null;
    const syntheticMark: MarksSelected = {
      nInterval: accion.nInterval,
      nType_Mark: accion.nType_Mark,
      label: '',
      sOriginalHour: accion.sOriginalHour,
    };
    const otherAcciones = this.acciones.filter(a => a !== accion);
    const isNextDay = !!accion.dDate && moment(accion.dDate).format('YYYY-MM-DD') !== this.actionDate.format('YYYY-MM-DD');
    return computeNewPosition(accion.sHour, this.actionDate, isNextDay, syntheticMark, this.intervals, otherAcciones);
  }

  getVisualIndex(nInterval: number): number {
    const uniqueIntervals = new Set<number>();
    this.intervals.forEach(inv => uniqueIntervals.add(inv.nInterval));
    this.acciones.forEach(acc => uniqueIntervals.add(acc.nInterval));
    uniqueIntervals.add(nInterval);
    const sortedIntervals = Array.from(uniqueIntervals).sort((a, b) => a - b);

    return sortedIntervals.indexOf(nInterval) + 1;
  }

  addNewMark(): void {
    const intervalStatus = new Map<number, { hasStart: boolean; hasEnd: boolean }>();
    this.intervals.forEach(inv => {
      intervalStatus.set(inv.nInterval, {
        hasStart: !!inv.inicio,
        hasEnd: !!inv.fin
      });
    });

    this.acciones.forEach(acc => {
      let status = intervalStatus.get(acc.nInterval) || { hasStart: false, hasEnd: false };
      if (acc.actionType === 'add') {
        if (acc.nType_Mark === 1) status.hasStart = true;
        if (acc.nType_Mark === 2) status.hasEnd = true;
      } else if (acc.actionType === 'delete') {
        if (acc.nType_Mark === 1) status.hasStart = false;
        if (acc.nType_Mark === 2) status.hasEnd = false;
      }
      intervalStatus.set(acc.nInterval, status);
    });

    let siguienteInterval = 1;
    let siguienteTipo = 1;
    let encontroHueco = false;
    const maxInterval = Math.max(0, ...Array.from(intervalStatus.keys()));

    for (let i = 1; i <= maxInterval; i++) {
      const status = intervalStatus.get(i);
      if (status) {
        if (status.hasStart && !status.hasEnd) {
          siguienteInterval = i;
          siguienteTipo = 2;
          encontroHueco = true;
          break;
        }
        if (!status.hasStart && status.hasEnd) {
          siguienteInterval = i;
          siguienteTipo = 1;
          encontroHueco = true;
          break;
        }
      }
    }
    if (!encontroHueco) {
      siguienteInterval = maxInterval + 1;
      siguienteTipo = 1;
    }
    this.markSelected = {
      nInterval: siguienteInterval,
      nIndex: this.getVisualIndex(siguienteInterval),
      nType_Mark: siguienteTipo,
      label: siguienteTipo === 1 ? 'Inicio' : 'Fin',
      isNextDay: false
    };
    this.esEliminar = false;
    this.actionDate = moment(this.selectedDate);
    this.accionForm.reset();
    this.syncDateLock();
  }

  onMarkDate(newDate: moment.Moment): void {
    this.actionDate = newDate;
  }

  limpiarAcciones(): void {
    this.acciones = [];
    this.cancelAction();
    this.actualizarValidacionIntervalos();
    this.syncDateLock();
  }

  imEmulate(currentWidth: number, realWidth: number, trueProps: string[]) {
    if (currentWidth > realWidth && trueProps.includes('isDesktop') && realWidth < 792) {
      return 1;
    }
    return 0;
  }

  private toIsoDateTime(date: Date | string): string {
    return moment(date).format('YYYY-MM-DDTHH:mm:ss');
  }

  private buildFinalMark(
    nInterval: number,
    nType_Mark: number,
    nId_Mark: number | null,
    dTiempo_Marca: string,
    bIs_Extemporanea: number,
    nAccion: number,
    sMotivo: string | null
  ): FinalMark {
    return { nInterval, nType_Mark, nId_Mark, dTiempo_Marca, bIs_Extemporanea, nAccion, sMotivo };
  }

  sendRequest(): void {
    if (this.acciones.length === 0 || this.intervalValidationError || this.debeEliminarMarcaFuera) return;
    this.isLoadingIntervals = true;

    // 1. Obtener Geolocalización
    let latitude: number | undefined | null;
    let longitude: number | undefined | null;

    this._intervalService.eventLocation$.pipe(take(1)).subscribe(location => {
      latitude = location?.latitude;
      longitude = location?.longitude;
    });

    // 2. Construir objeto de Seguridad del Dispositivo
    let a = navigator.userAgent.replace(' (', '-').replace(') ', '-');
    let b = a.replace(' (', '-').replace(') ', '-').split('-');

    const deviceInfo: DeviceInfo = {
      latitude: latitude,
      longitude: longitude,
      sBrowser: b.at(-1) || '',
      sUid: this.uuid,
      nWidth: this.deviceData.resolution[0],
      nHeight: this.deviceData.resolution[1],
      sOS: b[1],
      sIp: this.uuid,
      bEmulated: this.imEmulate(window.innerWidth, this.deviceData.resolution[0], this.trueProps),
      nMethod: this.deviceData.resolution[0] > 792 ? 9 : 2
    };

    const payloads = this.buildRequestPayloads(deviceInfo);

    this.smartAlert(
      '¡Atención!',
      '¿Enviar solicitudes de marcas?'
    ).subscribe((result: SmartAlertResult) => {
      if (result.confirmStatus) {
        this.spinner = true;
        this.sendPayloads(payloads, 0);
      } else {
        this.isLoadingIntervals = false;
      }
    });
  }

  private buildRequestPayloads(deviceInfo: DeviceInfo): RequestExtempPayload[] {
    const order: Array<'delete' | 'edit' | 'add'> = ['delete', 'edit', 'add'];
    const groups = order
      .map(type => this.acciones.filter(accion => accion.actionType === type))
      .filter(group => group.length > 0);

    if (groups.length <= 1) {
      return [this.composePayload(this.acciones, deviceInfo, false)];
    }

    return groups.map(group => this.composePayload(group, deviceInfo, true));
  }

  private composePayload(
    accionesAplicar: AccionAgregada[],
    deviceInfo: DeviceInfo,
    split: boolean
  ): RequestExtempPayload {
    const applyingDelete = accionesAplicar.some(accion => accion.actionType === 'delete');
    const deletedKeys = new Set(
      this.acciones
        .filter(accion => accion.actionType === 'delete')
        .map(accion => `${accion.nInterval}-${accion.nType_Mark}`)
    );
    const finalMarks: FinalMark[] = [];

    this.intervals.forEach((inv: IntervalView) => {
      const pushSlot = (mark: IntervalView['inicio'], type: number) => {
        if (!mark) return;
        const key = `${inv.nInterval}-${type}`;
        if (split && !applyingDelete && deletedKeys.has(key)) return;
        finalMarks.push(this.buildFinalMark(
          inv.nInterval,
          type,
          mark.nId ?? null,
          this.toIsoDateTime(mark.dTiempo_Marca),
          mark.bIs_Extemporanea ? 1 : 0,
          MARKS_ACTIONS.NO_ACTIONS,
          null
        ));
      };
      pushSlot(inv.inicio, 1);
      pushSlot(inv.fin, 2);
    });

    accionesAplicar.forEach((accion: AccionAgregada) => {
      const existingIndex = finalMarks.findIndex(
        m => m.nInterval === accion.nInterval && m.nType_Mark === accion.nType_Mark
      );

      if (accion.actionType === 'delete') {
        if (existingIndex > -1) {
          finalMarks[existingIndex].nAccion = MARKS_ACTIONS.DELETE;
          finalMarks[existingIndex].sMotivo = accion.sReason;
        }
      } else if (accion.actionType === 'edit') {
        if (existingIndex > -1) {
          finalMarks[existingIndex].dTiempo_Marca = this.toIsoDateTime(accion.dDate);
          finalMarks[existingIndex].bIs_Extemporanea = 1;
          finalMarks[existingIndex].nAccion = MARKS_ACTIONS.EDIT;
          finalMarks[existingIndex].sMotivo = accion.sReason;
        }
      } else if (accion.actionType === 'add') {
        finalMarks.push(this.buildFinalMark(
          accion.nInterval,
          accion.nType_Mark,
          null,
          this.toIsoDateTime(accion.dDate),
          1,
          MARKS_ACTIONS.ADD,
          accion.sReason
        ));
      }
    });

    const actionType = accionesAplicar[0]?.actionType;
    const nId_Tipo_Solicitud = actionType === 'add'
      ? REQUEST_TYPE.REGISTRO_MARCAS
      : actionType === 'delete'
        ? REQUEST_TYPE.ELIMINACION_MARCAS
        : REQUEST_TYPE.EDICION_MARCAS;
    const idUsuario = Number(localDB.getItem('idUsuario'));
    const selectedDateFormatted = moment(this.selectedDate).format('YYYY-MM-DD');

    return {
      nId_Mark: null,
      nId_Usuario: idUsuario,
      nId_Colaborador: this.nId_Colaborador,
      nId_Tipo_Solicitud,
      nId_Tipo_Motivo: 17,
      dFecha_Inicio: selectedDateFormatted,
      dFecha_Fin: selectedDateFormatted,
      sMotivo: accionesAplicar.map(accion => `[Int ${accion.nInterval}]: ${accion.sReason}`).join(' | '),
      nUsuario_Creador: idUsuario,
      isExtemp: true,
      deviceInfo,
      aExtemp: finalMarks
    };
  }

  private sendPayloads(payloads: RequestExtempPayload[], index: number): void {
    this._requestsService.requestExtemp(payloads[index]).subscribe({
      next: (response: GenericResponse<RequestExtempPayload>) => {
        if (!response.isSuccess) {
          this.spinner = false;
          this.isLoadingIntervals = false;
          this._smartAlertService.init({
            type: "warning",
            showCancelButton: false,
            message: index > 0
              ? `La solicitud anterior se envió. ${response.message}`
              : response.message,
          });
          if (index > 0) this.dropSentActions(payloads, index);
          return;
        }
        if (index + 1 < payloads.length) {
          this.sendPayloads(payloads, index + 1);
          return;
        }
        this.spinner = false;
        this._smartAlertService.init({
          type: "success",
          showCancelButton: false,
          timer: 1.5,
          timerProgressBar: true,
        });
        this.limpiarAcciones();
        this._intervalService.ReloadLocalDB(true);
        this._intervalService.ReloadDashboard(true);
        this.goBack.emit();
      },
      error: (e) => {
        this.spinner = false;
        this.isLoadingIntervals = false;
        this._smartAlertService.init({
          type: "danger",
          showCancelButton: false,
          message: e.error?.message || "Ocurrió un error inesperado.",
        });
        if (index > 0) this.dropSentActions(payloads, index);
      }
    });
  }

  private dropSentActions(payloads: RequestExtempPayload[], sentCount: number): void {
    const sentTypes = new Set(payloads.slice(0, sentCount).map(payload => payload.nId_Tipo_Solicitud));
    this.acciones = this.acciones.filter(accion => !sentTypes.has(this.requestTypeOf(accion.actionType)));
    this.actualizarValidacionIntervalos();
    this.syncDateLock();
  }

  private requestTypeOf(actionType: AccionAgregada['actionType']): number {
    if (actionType === 'add') return REQUEST_TYPE.REGISTRO_MARCAS;
    if (actionType === 'delete') return REQUEST_TYPE.ELIMINACION_MARCAS;
    return REQUEST_TYPE.EDICION_MARCAS;
  }

  actualizarValidacionIntervalos(): void {
    this.debeEliminarMarcaFuera = marcasExistentesFueraDeJornada(
      this.selectedDate,
      this.intervals,
      this.acciones
    ).length > 0;
    const solape = this.acciones
      .filter(accion => accion.actionType !== 'delete' && !!accion.dDate)
      .map(accion => validarSolapeJornadaVecina(moment(accion.dDate), this.adjacentIntervals))
      .find(mensaje => !!mensaje) ?? '';
    this.intervalValidationError = validarInicioEnOtraJornada(
      this.selectedDate,
      this.intervals,
      this.acciones
    ) || solape || (this.debeEliminarMarcaFuera ? '' : validarRequisitoIntervalos(
      this.selectedDate,
      this.intervals,
      this.acciones
    ));
    this._cdr.detectChanges()
  }

  smartAlert(tittle: string, message: string) {
    return this._smartAlertService.init({
      type: 'quest',
      title: tittle,
      message: message,
      showCancelButton: true,
      showConfirmButton: true,
    })
  }
}